import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetCollect, apiDoCollect } from '../../api/collect.api';
import MDBadge from 'components/MDBadge';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MDAlert from 'components/MDAlert';

const Collect = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const { user, appIsReady } = useData();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  const [showAlert, setShowAlert] = useState(false);
  const [capacity, setCapacity] = useState('');
  const [fee, setFee] = useState('');
  const [selectedMachine, setSelectedMachine] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (appIsReady && user) {
      fetchMachines(page);
    }
  }, [page, user, appIsReady]);

  const fetchMachines = async (pageNum) => {
    setLoading(true);
    try {
      const response = await apiGetCollect(pageNum, 10, user.accessToken);
      if (response && response.data) {
        setMachines(response.data);
        setTotal(response.total);
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ').slice(0, 10);
  };

  const handleEditClick = (machine) => {
    setSelectedMachine(machine);
    setShowAlert(true);
  };

  const confirmEdit = async () => {
    const params = {
      id: selectedMachine.id,
      machine_id: selectedMachine.petmachine.id,
      capacity,
      fee,
    };
    const response = await apiDoCollect(params, user.accessToken);

    if (response === 'success') {
      setSnackbarMessage('회수 처리되었습니다. 이제 Online 상태로 표시됩니다.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } else {
      console.log('No data received');
    }
    setShowAlert(false);
    fetchMachines(page);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const rows = machines.map(machine => {
    const petMachine = machine.petmachine || {};
    console.log("capa=" + machine.capacity);
    return {
      id: machine.id,
      device_no: petMachine.device_no || '',
      device_name: petMachine.device_name || '',
      branch_id: petMachine.branch_id || '',
      m_owner_userid: petMachine.m_owner_userid || '',
      setup_date: petMachine.setup_date ? formatDate(petMachine.setup_date) : '',
      address: petMachine.address || '',
      sido: petMachine.sido || '',
      memo: petMachine.memo || '',
      last_signal_date: petMachine.last_signal_date ? formatDate(petMachine.last_signal_date) : '',
      last_empty_date: petMachine.last_empty_date ? formatDate(petMachine.last_empty_date) : '',
      capacity: machine.capacity,
      fee: machine.fee,
      status: petMachine.status === 'on' ? 
        <MDBox ml={-1}>
          <MDBadge badgeContent="online" color="success" variant="gradient" size="sm" />
        </MDBox> : petMachine.status === 'off' ? 
        <MDBox ml={-1}>
          <MDBadge badgeContent="offline" color="dark" variant="gradient" size="sm" />
        </MDBox> : 
        <MDBox ml={-1}>
          <MDBadge badgeContent="full" color="warning" variant="gradient" size="sm" />
        </MDBox>,
      use_time: petMachine.use_time || '',
      edit: 
        machine.status === 3 ? '회수완료' :
        <Button 
          variant="contained" 
          color="success" 
          size="small" 
          onClick={() => handleEditClick(machine)}
        >
          회수
        </Button>
    };
  });
  
  const columns = [
    { Header: "ID", accessor: "id",  width: '1%', align: "left" },
    { Header: "디바이스 이름", accessor: "device_name", align: "left" },
    { Header: "소유자 ID", accessor: "m_owner_userid", align: "left" },
    { Header: "지점 ID", accessor: "branch_id", align: "left" },
    { Header: "주소", accessor: "address",  align: "left" },
    { Header: "설치 날짜", accessor: "setup_date", align: "left" },
    { Header: "마지막 비움 날짜", accessor: "last_empty_date", align: "left" },
    { Header: "상태", accessor: "status", align: "left" },
    { Header: "용량", accessor: "capacity", align: "left" },
    { Header: "요금", accessor: "fee", align: "left" },
    { Header: "회수완료", accessor: "edit", align: "center" },
  ];

  const totalPages = Math.ceil(total / 10);

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  회수요청 목록
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {Array.from({ length: totalPages }, (_, index) => (
                    <MDPagination
                      item
                      key={index}
                      onClick={() => handlePageChange(index + 1)}
                      active={page === index + 1}
                    >
                      {index + 1}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {showAlert && (
        <MDAlert color="info" dismissibl>
          <MDBox display="flex" flexDirection="column" alignItems="center" color="white">
            <MDTypography variant="h6" color="white">중량(kg)와 금액을 입력하세요</MDTypography>
            <TextField 
              label="중량(kg)" 
              value={capacity} 
              onChange={(e) => setCapacity(e.target.value)} 
              margin="normal" 
              fullWidth 
              variant="outlined"
              color="white"
              InputProps={{ style: { backgroundColor: 'white' } }}
            />
            <TextField 
              label="금액" 
              value={fee} 
              onChange={(e) => setFee(e.target.value)} 
              margin="normal" 
              fullWidth 
              variant="outlined"
              color="primary"
              InputProps={{ style: { backgroundColor: 'white' } }}
            />
          </MDBox>
          <MDBox display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="success" onClick={confirmEdit} style={{ marginRight: '10px' }}>
              확인
            </Button>
            <Button variant="contained" color="error" onClick={() => setShowAlert(false)}>
              취소
            </Button>
          </MDBox>
        </MDAlert>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Collect;
